import ScoringEngine from './scoringEngine';

/**
 * AI-Powered Lead Ranking Service
 *
 * Implements comprehensive scoring algorithms from update.md document
 * for intelligent lead qualification and prioritization
 */
export class AILeadRankingService {

  /**
   * Calculate comprehensive lead score and priority ranking
   * @param {Object} submission - Complete submission data
   * @returns {Object} - Enhanced ranking with AI insights
   */
  static async calculateLeadRanking(submission) {
    try {
      console.log('🎯 Calculating AI lead ranking for submission:', submission.id);

      // Convert submission to user profile format
      const userProfile = this.convertSubmissionToProfile(submission);

      // Get country code for scoring
      const countryCode = this.getCountryCode(submission.country_selection?.selectedCountry);

      // Calculate enhanced pathway scores
      const scoringResults = await ScoringEngine.calculateAllPathwayScores(
        userProfile,
        submission.cv_data?.aiAnalysis,
        countryCode
      );

      // Calculate AI-powered lead priority score
      const priorityScore = await this.calculatePriorityScore(submission, scoringResults);

      // Generate AI insights and recommendations
      const aiInsights = await this.generateAIInsights(submission, scoringResults, priorityScore);

      // Determine sales urgency and timing
      const salesUrgency = this.calculateSalesUrgency(submission, scoringResults, priorityScore);

      // Calculate conversion probability
      const conversionProbability = this.calculateConversionProbability(priorityScore, scoringResults);

      return {
        submissionId: submission.id,
        calculatedAt: new Date().toISOString(),
        priorityScore: priorityScore,
        priorityLevel: this.getPriorityLevel(priorityScore.score),
        scoringResults: scoringResults,
        aiInsights: aiInsights,
        salesUrgency: salesUrgency,
        conversionProbability: conversionProbability,
        recommendedAction: this.getRecommendedAction(priorityScore, salesUrgency),
        followUpTimeline: this.getFollowUpTimeline(priorityScore, salesUrgency),
        competitiveAdvantages: aiInsights.competitiveAdvantages || [],
        riskFactors: aiInsights.riskFactors || [],
        estimatedValue: this.estimateLeadValue(priorityScore, submission),
        teamAssignment: this.getTeamAssignment(submission, scoringResults)
      };

    } catch (error) {
      console.error('Error calculating AI lead ranking:', error);
      return this.getDefaultRanking(submission.id);
    }
  }

  /**
   * Convert submission data to user profile format
   */
  static convertSubmissionToProfile(submission) {
    const cvAnalysis = submission.cv_data?.aiAnalysis || null;
    const educationInfo = submission.education_info || {};
    const languageInfo = submission.language_info || {};
    const financialInfo = submission.financial_info || {};
    const rawExperience = Array.isArray(submission.experience_info)
      ? submission.experience_info
      : submission.experience_info
        ? [submission.experience_info]
        : [];

    const estimatedExperienceYears = this.estimateExperienceYears(rawExperience, cvAnalysis);

    return {
      personalInfo: {
        firstName: submission.first_name,
        lastName: submission.last_name,
        email: submission.email,
        phone: submission.phone,
        dateOfBirth: submission.personal_info?.dateOfBirth,
        ageGroup: submission.personal_info?.ageGroup,
        nationality: submission.personal_info?.nationality,
        currentLocation: submission.personal_info?.currentLocation
      },
      education: this.normalizeEducationProfile(educationInfo),
      experience: this.normalizeExperienceProfile(rawExperience, estimatedExperienceYears),
      experienceSummary: {
        estimatedYears: estimatedExperienceYears,
        totalExperienceLabel: rawExperience[0]?.totalExperience || null,
        hasInternationalExperience: rawExperience[0]?.hasInternationalExperience || null,
        currentRole: rawExperience[0]?.jobTitle || null,
        industry: rawExperience[0]?.industry || null
      },
      languageSkills: this.normalizeLanguageProfile(languageInfo, cvAnalysis),
      financialInfo: this.normalizeFinancialProfile(financialInfo),
      countrySelection: submission.country_selection || {},
      timeline: {
        startTimeline: submission.personal_info?.startTimeline,
        urgency: submission.personal_info?.urgency,
        mainGoal: submission.personal_info?.mainGoal
      },
      cvAnalysis
    };
  }

  /**
   * Get ISO country code for scoring
   */
  static getCountryCode(selectedCountry) {
    const countryMapping = {
      'germany': 'DEU',
      'canada': 'CAN',
      'australia': 'AUS',
      'uk': 'GBR',
      'united kingdom': 'GBR',
      'not sure': 'DEU',
      'notsure': 'DEU'
    };
    return countryMapping[selectedCountry?.toLowerCase()] || 'DEU';
  }

  /**
   * Calculate opportunity score (same as Sales Intelligence)
   * @param {Object} submissionData - Submission data
   * @param {Object} scores - Scoring results
   * @returns {number} - Opportunity score out of 100
   */
  static calculateOpportunityScore(submissionData, scores, leadActivities = []) {
    let score = 0;

    // 1. Pathway fit (up to 30)
    const bestPathway = scores?.bestMatch?.result;
    if (bestPathway?.percentageScore) {
      score += Math.min(bestPathway.percentageScore, 30);
    }

    // 2. Pathway breadth bonus (up to 6)
    const eligiblePathways = Object.values(scores?.pathways || {}).filter(pathway => pathway?.isEligible);
    if (eligiblePathways.length > 1) {
      score += Math.min((eligiblePathways.length - 1) * 3, 6);
    }

    // 3. Financial readiness (up to 20)
    const hasBudget = !!submissionData.financial_info?.educationBudget;
    const annualIncome = submissionData.financial_info?.annualIncome;
    if (hasBudget && annualIncome) {
      score += 20;
    } else if (hasBudget || annualIncome) {
      score += 14;
    } else {
      score += 6;
    }

    // 4. Timeline urgency (up to 12)
    const startTimeline = submissionData.personal_info?.startTimeline || '';
    const normalizedTimeline = startTimeline.toLowerCase();
    if (normalizedTimeline.includes('within 3 months') || normalizedTimeline.includes('3 months')) {
      score += 12;
    } else if (normalizedTimeline.includes('6 months') || normalizedTimeline.includes('3-6')) {
      score += 9;
    } else if (normalizedTimeline.includes('12 months') || normalizedTimeline.includes('year')) {
      score += 6;
    } else {
      score += 3;
    }

    // 5. CV strength (up to 15)
    if (submissionData.cv_data?.aiAnalysis) {
      const cvAnalysis = submissionData.cv_data.aiAnalysis;
      const educationLevel = cvAnalysis.educationAssessment?.level || '';
      const totalYears = parseFloat(cvAnalysis.experienceAnalysis?.totalYears) || 0;

      if (/phd|doctor/i.test(educationLevel)) {
        score += 9;
      } else if (/master/i.test(educationLevel)) {
        score += 7;
      } else if (/bachelor/i.test(educationLevel)) {
        score += 5;
      } else {
        score += 2;
      }

      if (totalYears >= 5) {
        score += 6;
      } else if (totalYears >= 3) {
        score += 4;
      } else if (totalYears >= 1) {
        score += 2;
      } else {
        score += 1;
      }
    } else {
      score += 5;
    }

    // 6. Engagement level (up to 7)
    const activityCount = Array.isArray(leadActivities) ? leadActivities.length : 0;
    if (activityCount >= 5) {
      score += 7;
    } else if (activityCount >= 3) {
      score += 5;
    } else if (activityCount >= 1) {
      score += 3;
    } else {
      score += 1;
    }

    // 7. Preferred communication (up to 5)
    const preference = submissionData.contact_preference;
    if (preference === 'whatsapp' || preference === 'phone') {
      score += 5;
    } else if (preference === 'email') {
      score += 3;
    } else {
      score += 1;
    }

    return Math.min(Math.round(score), 100);
  }

  /**
   * Calculate AI-powered priority score (0-100)
   */
  static async calculatePriorityScore(submission, scoringResults) {
    let score = 0;
    const breakdown = {};
    const factors = [];

    try {
      // Use the same opportunity score calculation as Sales Intelligence
      const activityLog = submission.lead_activities || submission.activities || [];
      const opportunityScore = this.calculateOpportunityScore(submission, scoringResults, activityLog);
      score = opportunityScore;
      factors.push(`Opportunity score: ${opportunityScore}/100`);

      // Profile completeness contributes up to 5 points
      const completenessScore = this.calculateProfileCompleteness(submission);
      breakdown.profileCompleteness = completenessScore;
      score += completenessScore;
      factors.push(`Profile completeness: ${completenessScore}/5`);

      // Track financial readiness for later calculations
      const financialReadiness = this.calculateFinancialScore(submission);
      breakdown.financial = financialReadiness;
      factors.push(`Financial readiness: ${financialReadiness}/15`);

      // Additional AI factors for lead prioritization beyond basic opportunity score
      // 1. Sales Urgency (15 points)
      const urgencyScore = this.calculateSalesUrgencyScore(submission, scoringResults);
      breakdown.salesUrgency = urgencyScore;
      score += urgencyScore;
      factors.push(`Sales urgency: ${urgencyScore}/15`);

      // 2. Market Timing (10 points)
      const timingScore = this.calculateMarketTimingScore(submission, scoringResults);
      breakdown.marketTiming = timingScore;
      score += timingScore;
      factors.push(`Market timing: ${timingScore}/10`);

      // 3. Competitive Advantage (5 points)
      const competitiveScore = this.calculateCompetitiveAdvantageScore(submission, scoringResults);
      breakdown.competitiveAdvantage = competitiveScore;
      score += competitiveScore;
      factors.push(`Competitive advantage: ${competitiveScore}/5`);

      // Cap at 100 and provide grade
      const finalScore = Math.min(Math.round(score), 100);
      const bestPathwayPercentage = scoringResults.bestMatch?.result?.percentageScore || 0;

      return {
        score: finalScore,
        maxScore: 100,
        breakdown: breakdown,
        factors: factors,
        grade: this.getScoreGrade(finalScore),
        basePathwayScore: bestPathwayPercentage, // Store the original pathway score for display
        opportunityScore: opportunityScore // Store the opportunity score for consistency
      };

    } catch (error) {
      console.error('Error calculating priority score:', error);
      return { score: 50, maxScore: 100, breakdown: {}, factors: ['Calculation error'], grade: 'C' };
    }
  }

  /**
   * Calculate sales urgency score
   */
  static calculateSalesUrgencyScore(submission, scoringResults) {
    let score = 0;

    // Recent submission urgency
    if (submission?.created_at) {
      const submissionDate = new Date(submission.created_at);
      if (!Number.isNaN(submissionDate.getTime())) {
        const daysSinceSubmission = (Date.now() - submissionDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceSubmission <= 7) score += 5;
        else if (daysSinceSubmission <= 30) score += 3;
      }
    }

    // Timeline urgency from user intent
    const timeline = submission.personal_info?.startTimeline?.toLowerCase() || '';
    if (timeline.includes('within 3 months') || timeline.includes('3 months')) {
      score += 4;
    } else if (timeline.includes('3-6') || timeline.includes('6 months')) {
      score += 2;
    }

    // Financial readiness urgency
    const educationBudget = this.parseNumericValue(submission.financial_info?.educationBudget);
    const annualIncome = this.parseNumericValue(submission.financial_info?.annualIncome);
    if (educationBudget >= 10000 || annualIncome >= 10000) {
      score += 4;
    } else if (submission.financial_info?.fundingType === 'self/family funds' || submission.financial_info?.fundingType === 'sponsorship') {
      score += 2;
    }

    // High-demand pathway urgency
    const pathwayKey = (scoringResults.bestMatch?.pathway || '').toLowerCase();
    if (pathwayKey.includes('student') || pathwayKey.includes('work') || pathwayKey.includes('express') || pathwayKey.includes('skilled')) {
      score += 4;
    }

    return Math.min(score, 15);
  }

  /**
   * Calculate market timing score
   */
  static calculateMarketTimingScore(submission, scoringResults) {
    let score = 0;

    // Seasonal application cycles
    const month = new Date().getMonth();
    if (month >= 8 && month <= 12) score += 5; // Peak application season

    // Education cycle alignment
    const gradYear = parseInt(submission.education_info?.graduationYear, 10);
    const currentYear = new Date().getFullYear();
    if (!Number.isNaN(gradYear) && gradYear >= currentYear) {
      score += 3;
    }

    const urgency = submission.personal_info?.urgency?.toLowerCase();
    if (urgency && (urgency.includes('urgent') || urgency.includes('next intake'))) {
      score += 2;
    }

    return Math.min(score, 10);
  }

  /**
   * Calculate competitive advantage score
   */
  static calculateCompetitiveAdvantageScore(submission, scoringResults) {
    let score = 0;

    // CV analysis advantage
    if (submission.cv_data?.aiAnalysis) score += 2;

    // Language skills advantage
    const englishLevel = submission.language_info?.englishSpeaking?.toLowerCase();
    if (englishLevel === 'fluent' || englishLevel === 'native' || englishLevel === 'advanced') {
      score += 2;
    }

    // Work experience advantage
    const experienceEntries = Array.isArray(submission.experience_info)
      ? submission.experience_info
      : submission.experience_info
        ? [submission.experience_info]
        : [];
    if (experienceEntries.length > 0) {
      score += 1;
    }

    const cvYears = submission.cv_data?.aiAnalysis?.experienceAnalysis?.totalYears || 0;
    if (cvYears >= 5) {
      score += 1;
    }

    return Math.min(score, 5);
  }

  /**
   * Calculate urgency score based on timeline and behavior
   */
  static calculateUrgencyScore(submission) {
    let score = 0;
    const timeline = submission.personal_info?.startTimeline;
    const urgency = submission.personal_info?.urgency;

    // Timeline scoring (12 points)
    const timelineScores = {
      'within 3 months': 12,
      '3-6 months': 10,
      '6-12 months': 7,
      '1+ year': 4,
      'just exploring': 2
    };
    score += timelineScores[timeline] || 5;

    // Urgency level scoring (8 points)
    const urgencyScores = {
      'very urgent - next intake': 8,
      'planning ahead - specific timeline': 6,
      'gathering information - no rush': 3
    };
    score += urgencyScores[urgency] || 4;

    return Math.min(score, 20);
  }

  /**
   * Calculate financial readiness score
   */
  static calculateFinancialScore(submission) {
    let score = 0;
    const financial = submission.financial_info || {};

    // Funding type (8 points)
    const fundingScores = {
      'self/family funds': 8,
      'education loan': 6,
      'scholarship': 7,
      'sponsorship': 8,
      'need financial guidance': 3
    };
    score += fundingScores[financial.fundingType] || 3;

    // Budget indication (5 points)
    if (financial.budgetRange && financial.budgetRange !== 'not specified') {
      score += 5;
    } else if (financial.annualIncome) {
      score += 3;
    }

    // Sponsorship advantage (2 points)
    if (financial.hasSponsorship) {
      score += 2;
    }

    return Math.min(score, 15);
  }

  /**
   * Calculate profile completeness score
   */
  static calculateProfileCompleteness(submission) {
    let completeness = 0;
    const totalFields = 10;
    let completedFields = 0;

    // Check key fields
    if (submission.first_name && submission.last_name) completedFields++;
    if (submission.email && submission.phone) completedFields++;
    if (submission.personal_info?.ageGroup) completedFields++;
    if (submission.education_info?.highestEducation) completedFields++;
    if (submission.country_selection?.selectedCountry) completedFields++;
    if (submission.financial_info?.fundingType) completedFields++;
    if (submission.personal_info?.startTimeline) completedFields++;
    if (submission.personal_info?.urgency) completedFields++;
    if (submission.personal_info?.mainGoal) completedFields++;
    if (submission.cv_data?.url) completedFields++; // CV uploaded

    completeness = Math.round((completedFields / totalFields) * 5);
    return completeness;
  }

  /**
   * Generate AI insights and recommendations
   */
  static async generateAIInsights(submission, scoringResults, priorityScore) {
    const insights = {
      strengths: [],
      concerns: [],
      opportunities: [],
      recommendations: [],
      competitiveAdvantages: [],
      riskFactors: [],
      personalizedApproach: ''
    };

    try {
      const bestMatch = scoringResults?.bestMatch;
      const bestPathway = bestMatch?.result;

      if (bestPathway) {
        const pathwayName = this.formatPathwayDisplayName(bestPathway.pathwayName || bestMatch.pathway);
        const successRate = typeof bestPathway.successRate === 'number'
          ? `${bestPathway.successRate}% success rate`
          : null;
        const confidence = typeof bestPathway.confidence === 'number'
          ? `${Math.round(bestPathway.confidence * 100)}% confidence`
          : null;

        insights.competitiveAdvantages.push(
          `Strong alignment with ${pathwayName}${successRate ? ` (${successRate}${confidence ? ` · ${confidence}` : ''})` : ''}`
        );

        if (bestPathway.processingDays) {
          insights.opportunities.push(
            `${pathwayName} processing timeline: ${bestPathway.processingDays} days`
          );
        }

        const breakdown = bestPathway.breakdown || {};
        for (const [criterionKey, detail] of Object.entries(breakdown)) {
          const strength = this.describeCriterionStrength(criterionKey, detail);
          if (strength) insights.strengths.push(strength);

          const concern = this.describeCriterionConcern(criterionKey, detail);
          if (concern) insights.concerns.push(concern);
        }

        // Include AI-derived recommendations and matched rules if available
        (bestPathway.recommendations || []).forEach(rec => {
          if (!rec) return;
          if (typeof rec === 'string') insights.recommendations.push(rec);
          else if (rec.message) insights.recommendations.push(rec.message);
        });

        (bestPathway.matchedRules || []).forEach(rule => {
          if (rule?.description) insights.opportunities.push(rule.description);
        });
      }

      // Consider other strong pathways as backup options
      const alternativePathways = Object.values(scoringResults?.pathways || {})
        .filter(pathway =>
          pathway?.pathwayKey !== bestPathway?.pathwayKey &&
          pathway?.percentageScore >= 50
        )
        .sort((a, b) => (b?.percentageScore || 0) - (a?.percentageScore || 0))
        .slice(0, 2);

      alternativePathways.forEach(pathway => {
        const name = this.formatPathwayDisplayName(pathway.pathwayName || pathway.pathwayKey);
        insights.opportunities.push(
          `${name} is a viable alternative (${pathway.percentageScore}% match)`
        );
      });

      // Priority-based insights
      if (priorityScore.score >= 80) {
        insights.personalizedApproach = 'High-value lead requiring immediate attention with premium package offering';
        insights.recommendations.push('Schedule consultation within 24 hours');
        insights.recommendations.push('Prepare premium package proposal');
      } else if (priorityScore.score >= 65) {
        insights.personalizedApproach = 'Strong lead with good conversion potential';
        insights.recommendations.push('Contact within 2-3 business days');
        insights.recommendations.push('Focus on addressing key concerns');
      } else if (priorityScore.score >= 50) {
        insights.personalizedApproach = 'Moderate potential - needs qualification';
        insights.recommendations.push('Send informational resources first');
        insights.recommendations.push('Schedule follow-up in 1 week');
      } else {
        insights.personalizedApproach = 'Low priority - nurture campaign';
        insights.recommendations.push('Add to email nurture sequence');
        insights.recommendations.push('Monitor for profile improvements');
      }

      // Timeline-based opportunities
      const timeline = submission.personal_info?.startTimeline;
      if (timeline && timeline.includes('3 months')) {
        insights.opportunities.push('Urgent timeline may increase conversion speed');
        insights.recommendations.push('Expedite documentation process');
      }

      // Financial-based insights
      const financial = submission.financial_info || {};
      if (financial.hasSponsorship) {
        insights.competitiveAdvantages.push('Sponsorship advantage reduces financial barriers');
      }

      // CV-based insights
      if (submission.cv_data?.url) {
        insights.competitiveAdvantages.push('CV available for detailed assessment');
        if (submission.cv_data?.aiAnalysis) {
          insights.opportunities.push('AI analysis available for personalized matching');
        }
      }

      // Normalize and deduplicate outputs
      insights.strengths = this.uniqueNonEmpty(insights.strengths);
      insights.concerns = this.uniqueNonEmpty(insights.concerns);
      insights.opportunities = this.uniqueNonEmpty(insights.opportunities);
      insights.recommendations = this.uniqueNonEmpty(insights.recommendations);
      insights.competitiveAdvantages = this.uniqueNonEmpty(insights.competitiveAdvantages);

    } catch (error) {
      console.error('Error generating AI insights:', error);
    }

    return insights;
  }

  /**
   * Calculate sales urgency based on multiple factors
   */
  static calculateSalesUrgency(submission, scoringResults, priorityScore) {
    let urgencyScore = 0;
    const reasons = [];

    // High priority score
    if (priorityScore.score >= 80) {
      urgencyScore += 35;
      reasons.push('High priority score (80+)');
    } else if (priorityScore.score >= 65) {
      urgencyScore += 25;
      reasons.push('Good priority score (65+)');
    }

    // Urgent timeline
    const timeline = submission.personal_info?.startTimeline;
    if (timeline) {
      const lowerTimeline = timeline.toLowerCase();
      if (lowerTimeline.includes('within 3 months') || lowerTimeline.includes('3 months')) {
        urgencyScore += 25;
        reasons.push('Urgent start timeline');
      } else if (lowerTimeline.includes('3-6') || lowerTimeline.includes('6 months')) {
        urgencyScore += 15;
        reasons.push('Planned intake within 6 months');
      }
    }

    // High conversion probability
    const bestPathway = scoringResults.bestMatch?.result;
    if (bestPathway) {
      if ((bestPathway.percentageScore || 0) >= 75) {
        urgencyScore += 15;
        reasons.push('Strong pathway match (75%+)');
      }
      if ((bestPathway.successRate || 0) >= 80) {
        urgencyScore += 10;
        reasons.push('Pathway has high success rate');
      }
      if ((bestPathway.confidence || 0) >= 0.75) {
        urgencyScore += 5;
        reasons.push('High assessment confidence');
      }
    }

    // Multiple eligible pathways increase urgency
    const eligiblePathways = Object.values(scoringResults.pathways || {}).filter(pathway => pathway?.isEligible);
    if (eligiblePathways.length > 1) {
      urgencyScore += 5;
      reasons.push('Multiple qualified pathways available');
    }

    // Financial readiness
    const financial = submission.financial_info || {};
    if (financial.hasSponsorship === 'yes' || financial.hasSponsorship === true) {
      urgencyScore += 10;
      reasons.push('Financially ready');
    } else if (financial.fundingType === 'self/family funds' || financial.fundingType === 'sponsorship') {
      urgencyScore += 8;
      reasons.push('Self-funded applicant');
    } else if (financial.fundingType === 'education loan') {
      urgencyScore += 5;
      reasons.push('Education loan path identified');
    }

    return {
      score: Math.min(urgencyScore, 100),
      level: this.getUrgencyLevel(urgencyScore),
      reasons: reasons,
      timeframe: this.getUrgencyTimeframe(urgencyScore)
    };
  }

  /**
   * Calculate comprehensive conversion probability
   */
  static calculateConversionProbability(priorityScore, scoringResults) {
    let probability = priorityScore.score * 0.6;

    const bestPathway = scoringResults.bestMatch?.result;
    if (bestPathway?.successRate) {
      probability += bestPathway.successRate * 0.35;
    }
    if (typeof bestPathway?.confidence === 'number') {
      probability += Math.round(bestPathway.confidence * 100) * 0.15;
    }
    if ((bestPathway?.percentageScore || 0) >= 85) {
      probability += 6;
    }

    const completeness = priorityScore.breakdown?.profileCompleteness || 0;
    probability += completeness * 2.5;

    const financial = priorityScore.breakdown?.financial || 0;
    probability += (financial / 15) * 6;

    const urgency = priorityScore.breakdown?.salesUrgency || 0;
    probability += (urgency / 15) * 12;

    const timing = priorityScore.breakdown?.marketTiming || 0;
    probability += (timing / 10) * 6;

    const eligibleCount = Object.values(scoringResults.pathways || {}).filter(pathway => pathway?.isEligible).length;
    if (eligibleCount > 1) {
      probability += Math.min((eligibleCount - 1) * 4, 12);
    }

    const maxBoost = priorityScore.breakdown?.competitiveAdvantage || 0;
    probability += maxBoost;

    return Math.min(Math.max(Math.round(probability), 5), 98);
  }

  /**
   * Estimate lead value based on priority and submission data
   */
  static estimateLeadValue(priorityScore, submission) {
    let baseValue = 1200000; // Base value in LKR (approximately $3,500 USD)

    // Priority multiplier
    if (priorityScore.score >= 80) baseValue *= 2.5;
    else if (priorityScore.score >= 65) baseValue *= 1.8;
    else if (priorityScore.score >= 50) baseValue *= 1.2;

    // Country/service premium
    const country = submission.country_selection?.selectedCountry?.toLowerCase();
    if (country === 'germany') baseValue *= 1.2;
    else if (country === 'canada') baseValue *= 1.3;
    else if (country === 'australia') baseValue *= 1.4;

    // Package premium
    const selectedPackageRaw = submission.selected_package ||
      submission.review_data?.selectedPackage ||
      submission.review_data?.selected_package ||
      '';
    const selectedPackage = selectedPackageRaw.toString().toLowerCase();
    if (selectedPackage.includes('premium')) baseValue *= 1.5;
    else if (selectedPackage.includes('essential')) baseValue *= 1.2;

    // CV analysis premium
    const hasCvAnalysis = Boolean(submission.cv_data?.aiAnalysis);
    if (hasCvAnalysis) baseValue *= 1.2;

    return {
      estimatedValue: Math.round(baseValue),
      currency: 'LKR',
      factors: [
        `Priority score multiplier: ${priorityScore.score >= 80 ? '2.5x' : priorityScore.score >= 65 ? '1.8x' : priorityScore.score >= 50 ? '1.2x' : '1.0x'}`,
        `Country: ${country || 'unspecified'}`,
        `Package: ${selectedPackageRaw || 'none'}`,
        `CV analysis: ${hasCvAnalysis ? 'Yes' : 'No'}`
      ]
    };
  }

  /**
   * Get recommended action based on priority and urgency
   */
  static getRecommendedAction(priorityScore, salesUrgency) {
    if (priorityScore.score >= 80 && salesUrgency.score >= 70) {
      return {
        action: 'Immediate Call',
        description: 'Call within 2 hours, offer premium package',
        priority: 'Critical',
        nextStep: 'Schedule consultation with senior consultant'
      };
    } else if (priorityScore.score >= 65) {
      return {
        action: 'Priority Follow-up',
        description: 'Contact within 24 hours with detailed proposal',
        priority: 'High',
        nextStep: 'Send personalized pathway analysis'
      };
    } else if (priorityScore.score >= 50) {
      return {
        action: 'Standard Process',
        description: 'Add to regular follow-up sequence',
        priority: 'Medium',
        nextStep: 'Send educational content and schedule follow-up'
      };
    } else {
      return {
        action: 'Nurture Campaign',
        description: 'Add to long-term nurture sequence',
        priority: 'Low',
        nextStep: 'Monitor for engagement and profile improvements'
      };
    }
  }

  /**
   * Get team assignment based on submission and scoring
   */
  static getTeamAssignment(submission, scoringResults) {
    const country = submission.country_selection?.selectedCountry?.toLowerCase();
    const bestPathwayKey = (scoringResults.bestMatch?.pathway ||
      scoringResults.bestMatch?.result?.pathwayKey ||
      '').toLowerCase();

    // Country-based assignment
    if (country === 'germany') {
      return 'Germany Team';
    } else if (country === 'canada') {
      return 'Canada Team';
    } else if (country === 'australia') {
      return 'Australia Team';
    } else if (country === 'uk' || country === 'united kingdom') {
      return 'UK Team';
    } else if (country === 'usa' || country === 'united states') {
      return 'USA Team';
    }

    // Pathway-based assignment
    if (bestPathwayKey.includes('student')) {
      return 'Student Visa Team';
    } else if (bestPathwayKey.includes('chancenkarte') || bestPathwayKey.includes('work') || bestPathwayKey.includes('skilled')) {
      return 'Skilled Migration Team';
    } else if (bestPathwayKey.includes('express') || bestPathwayKey.includes('pnp')) {
      return 'Canada Immigration Team';
    }

    return 'General Consultation Team';
  }

  /**
   * Get follow-up timeline based on priority
   */
  static getFollowUpTimeline(priorityScore, salesUrgency) {
    if (priorityScore.score >= 80) {
      return 'Immediate (within 2 hours)';
    } else if (priorityScore.score >= 65) {
      return 'Within 24 hours';
    } else if (priorityScore.score >= 50) {
      return 'Within 3 business days';
    } else {
      return 'Within 1 week';
    }
  }

  // Helper methods
  static normalizeEducationProfile(educationInfo = {}) {
    const highestEducation = educationInfo.highestEducation || educationInfo.highestLevel || '';
    const normalizedLevel = this.normalizeEducationLevel(highestEducation);

    return {
      ...educationInfo,
      highestEducation,
      highestLevel: normalizedLevel,
      fieldOfStudy: educationInfo.fieldOfStudy || educationInfo.major || null,
      graduationYear: educationInfo.graduationYear || educationInfo.year || null
    };
  }

  static normalizeEducationLevel(level) {
    if (!level) return 'unknown';
    const value = level.toString().toLowerCase();
    if (value.includes('phd') || value.includes('doctor')) return 'phd';
    if (value.includes('master')) return 'masters';
    if (value.includes('bachelor')) return 'bachelors';
    if (value.includes('diploma')) return 'diploma';
    if (value.includes('certificate')) return 'certificate';
    if (value.includes('a-level') || value.includes('advanced level')) return 'a_levels';
    if (value.includes('high school') || value.includes('o-level') || value.includes('secondary')) return 'high_school';
    if (value.includes('still studying') || value.includes('undergraduate')) return 'studying';
    return value.replace(/[^a-z]+/g, ' ').trim() || 'unknown';
  }

  static normalizeLanguageProfile(languageInfo = {}, cvAnalysis = null) {
    const englishLevels = [
      languageInfo.englishSpeaking,
      languageInfo.englishReading,
      languageInfo.englishWriting,
      languageInfo.englishListening,
      cvAnalysis?.languageAnalysis?.englishLevel
    ].map(level => this.normalizeLanguageLevel(level)).filter(Boolean);

    const english = englishLevels.join(' ').trim() || 'unknown';
    const german = this.normalizeLanguageLevel(languageInfo.germanSpeaking || languageInfo.germanLevel || '');
    const hasIELTS = languageInfo.hasIELTS === 'yes' || languageInfo.hasIELTS === true;
    const ieltsScore = languageInfo.ieltsScore || cvAnalysis?.languageAnalysis?.ieltsScore || null;
    const descriptors = [
      english !== 'unknown' ? english : null,
      german,
      hasIELTS ? `ielts ${ieltsScore || '6.0+'}` : null
    ].filter(Boolean).join(' ');

    return {
      ...languageInfo,
      english,
      german,
      descriptors: descriptors || english,
      hasIELTS,
      ieltsScore
    };
  }

  static normalizeLanguageLevel(level) {
    if (!level) return '';
    const value = level.toString().toLowerCase();
    if (value.includes('native')) return 'native';
    if (value.includes('fluent')) return 'fluent';
    if (value.includes('advanced')) return 'advanced';
    if (value.includes('intermediate')) return 'intermediate';
    if (value.includes('basic') || value.includes('beginner')) return 'beginner';
    if (value.includes('ielts')) return value;
    return value;
  }

  static normalizeFinancialProfile(financialInfo = {}) {
    const educationBudget = this.parseNumericValue(financialInfo.educationBudget);
    const annualIncome = this.parseNumericValue(financialInfo.annualIncome);
    const monthlyIncome = annualIncome ? Math.round(annualIncome / 12) : 0;
    const fundsAvailable = educationBudget || this.parseNumericValue(financialInfo.fundsAvailable);

    return {
      ...financialInfo,
      educationBudget,
      annualIncome,
      monthlyIncome,
      fundsAvailable,
      budgetRange: financialInfo.budgetRange || null,
      fundingType: financialInfo.fundingType || null,
      hasSponsorship: financialInfo.hasSponsorship === 'yes' || financialInfo.hasSponsorship === true
    };
  }

  static normalizeExperienceProfile(rawExperience = [], estimatedYears = 0) {
    if (!Array.isArray(rawExperience) || rawExperience.length === 0) {
      if (!estimatedYears || estimatedYears <= 0) return [];
      return Array.from({ length: estimatedYears }, (_, idx) => ({
        entryIndex: idx + 1,
        estimatedYears
      }));
    }

    if (!estimatedYears || estimatedYears <= rawExperience.length) {
      return rawExperience;
    }

    const expanded = [];
    for (let i = 0; i < estimatedYears; i++) {
      const source = rawExperience[i] || rawExperience[rawExperience.length - 1];
      expanded.push({
        ...source,
        entryIndex: i + 1,
        estimatedYears
      });
    }
    return expanded;
  }

  static estimateExperienceYears(rawExperience = [], cvAnalysis = null) {
    if (cvAnalysis?.experienceAnalysis?.totalYears) {
      return Math.round(cvAnalysis.experienceAnalysis.totalYears);
    }

    const totalExperienceLabel = rawExperience?.[0]?.totalExperience?.toLowerCase() || '';
    if (!totalExperienceLabel) {
      return rawExperience?.length || 0;
    }

    const rangeMatch = totalExperienceLabel.match(/(\d+)\s*-\s*(\d+)/);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        return Math.round((min + max) / 2);
      }
    }

    const numberMatch = totalExperienceLabel.match(/(\d+)/);
    if (numberMatch) {
      const value = parseInt(numberMatch[1], 10);
      if (!Number.isNaN(value)) {
        if (totalExperienceLabel.includes('more than')) {
          return value + 2;
        }
        return value;
      }
    }

    if (totalExperienceLabel.includes('less than') || totalExperienceLabel.includes('<')) return 1;
    if (totalExperienceLabel.includes('no experience')) return 0;
    if (totalExperienceLabel.includes('more than')) return 5;

    return rawExperience?.length || 0;
  }

  static parseNumericValue(value) {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
      const parsed = parseFloat(cleaned);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  static formatPathwayDisplayName(pathwayName) {
    if (!pathwayName) return 'Immigration Pathway';
    const normalized = pathwayName.replace(/_/g, ' ').toLowerCase();
    return normalized.replace(/\b\w/g, char => char.toUpperCase());
  }

  static formatCriterionLabel(criterionKey) {
    if (!criterionKey) return 'profile area';
    return criterionKey
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  static describeCriterionStrength(criterionKey, detail = {}) {
    const percentage = typeof detail.percentage === 'number'
      ? detail.percentage
      : detail.maxPoints
        ? Math.round(((detail.score || 0) / detail.maxPoints) * 100)
        : detail.score || 0;

    if (percentage >= 70) {
      const label = this.formatCriterionLabel(criterionKey);
      return `Strong ${label} alignment (${percentage}% match)`;
    }

    return '';
  }

  static describeCriterionConcern(criterionKey, detail = {}) {
    const percentage = typeof detail.percentage === 'number'
      ? detail.percentage
      : detail.maxPoints
        ? Math.round(((detail.score || 0) / detail.maxPoints) * 100)
        : detail.score || 0;

    if (percentage > 0 && percentage <= 40) {
      const label = this.formatCriterionLabel(criterionKey);
      return `${label} needs improvement (${percentage}% match)`;
    }

    return '';
  }

  static uniqueNonEmpty(items = []) {
    const set = new Set();
    const result = [];

    items.forEach(item => {
      if (!item || typeof item !== 'string') return;
      const trimmed = item.trim();
      if (!trimmed) return;
      if (!set.has(trimmed)) {
        set.add(trimmed);
        result.push(trimmed);
      }
    });

    return result;
  }

  static getPriorityLevel(score) {
    if (score >= 80) return 'HIGH';
    else if (score >= 65) return 'MEDIUM-HIGH';
    else if (score >= 50) return 'MEDIUM';
    else if (score >= 35) return 'LOW-MEDIUM';
    else return 'LOW';
  }

  static getScoreGrade(score) {
    if (score >= 90) return 'A+';
    else if (score >= 80) return 'A';
    else if (score >= 70) return 'B+';
    else if (score >= 60) return 'B';
    else if (score >= 50) return 'C';
    else return 'D';
  }

  static getUrgencyLevel(score) {
    if (score >= 70) return 'Critical';
    else if (score >= 50) return 'High';
    else if (score >= 30) return 'Medium';
    else return 'Low';
  }

  static getUrgencyTimeframe(score) {
    if (score >= 70) return 'Immediate (2-4 hours)';
    else if (score >= 50) return 'Same day';
    else if (score >= 30) return '24-48 hours';
    else return '3-5 days';
  }

  static getDefaultRanking(submissionId) {
    return {
      submissionId: submissionId,
      calculatedAt: new Date().toISOString(),
      priorityScore: { score: 50, maxScore: 100, breakdown: {}, factors: ['Default ranking'], grade: 'C' },
      priorityLevel: 'MEDIUM',
      scoringResults: null,
      aiInsights: {
        strengths: [],
        concerns: ['Ranking calculation failed'],
        recommendations: ['Manual review required']
      },
      salesUrgency: { score: 30, level: 'Low', reasons: [], timeframe: '3-5 days' },
      conversionProbability: 45,
      recommendedAction: {
        action: 'Manual Review',
        description: 'Requires manual assessment',
        priority: 'Medium'
      }
    };
  }

  /**
   * Batch calculate rankings for multiple submissions
   */
  static async batchCalculateRankings(submissions) {
    const rankings = [];

    for (const submission of submissions) {
      try {
        const ranking = await this.calculateLeadRanking(submission);
        rankings.push(ranking);
      } catch (error) {
        console.error(`Error ranking submission ${submission.id}:`, error);
        rankings.push(this.getDefaultRanking(submission.id));
      }
    }

    // Sort by priority score
    return rankings.sort((a, b) => b.priorityScore.score - a.priorityScore.score);
  }

  /**
   * Get dashboard statistics for all submissions
   */
  static async getDashboardStatistics(submissions) {
    const rankings = await this.batchCalculateRankings(submissions);

    const stats = {
      totalSubmissions: submissions.length,
      highPriority: rankings.filter(r => r.priorityLevel === 'HIGH').length,
      mediumPriority: rankings.filter(r => r.priorityLevel.includes('MEDIUM')).length,
      lowPriority: rankings.filter(r => r.priorityLevel.includes('LOW')).length,
      averagePriorityScore: Math.round(rankings.reduce((sum, r) => sum + r.priorityScore.score, 0) / rankings.length),
      conversionProbability: Math.round(rankings.reduce((sum, r) => sum + r.conversionProbability, 0) / rankings.length),
      estimatedTotalValue: rankings.reduce((sum, r) => sum + (r.estimatedValue?.estimatedValue || 0), 0),
      topCountries: this.getTopCountries(submissions),
      urgencyBreakdown: this.getUrgencyBreakdown(rankings)
    };

    return stats;
  }

  static getTopCountries(submissions) {
    const countryCount = {};
    submissions.forEach(sub => {
      const country = sub.country_selection?.selectedCountry || 'Unknown';
      countryCount[country] = (countryCount[country] || 0) + 1;
    });

    return Object.entries(countryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([country, count]) => ({ country, count }));
  }

  static getUrgencyBreakdown(rankings) {
    const breakdown = {
      critical: rankings.filter(r => r.salesUrgency.level === 'Critical').length,
      high: rankings.filter(r => r.salesUrgency.level === 'High').length,
      medium: rankings.filter(r => r.salesUrgency.level === 'Medium').length,
      low: rankings.filter(r => r.salesUrgency.level === 'Low').length
    };

    return breakdown;
  }
}

export default AILeadRankingService;
